import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  getCatalog, createCostLine, updateCostLine, confirmCostLine, rejectCostLine, confirmAllCostLines, deleteCostLine
} from 'services/shipments.service'
import StatusBadge from 'components/StatusBadge'
import { LAYERS, ALLOCATION_BASES } from 'config'
import { toNumberOrNull } from 'utils/format'
import { useI18n, apiError } from 'i18n'

const COST_LAYERS = LAYERS.filter((l) => l.key !== 'merchandise')
const cellInput = 'w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none'

const blank = () => ({
  category: 'international_freight', conceptCode: '', conceptRaw: '', amount: '', currency: 'MXN',
  exchangeRate: '', vatAmount: '', allocationBasis: '', inCustomsValue: '', recoverable: ''
})

const toForm = (l) => ({
  category: l.category,
  conceptCode: l.conceptCode || '',
  conceptRaw: l.conceptRaw || '',
  amount: String(l.amount ?? ''),
  currency: l.currency || '',
  exchangeRate: l.exchangeRate == null ? '' : String(l.exchangeRate),
  vatAmount: l.vatAmount == null ? '' : String(l.vatAmount),
  allocationBasis: l.allocationBasis || '',
  inCustomsValue: l.inCustomsValue == null ? '' : String(l.inCustomsValue),
  recoverable: l.recoverable == null ? '' : String(l.recoverable)
})

const triState = (v) => (v === '' ? null : v === 'true')

const toBody = (f) => ({
  category: f.category,
  conceptCode: f.conceptCode || null,
  conceptRaw: f.conceptRaw || null,
  amount: toNumberOrNull(f.amount),
  currency: f.currency.toUpperCase(),
  exchangeRate: toNumberOrNull(f.exchangeRate),
  vatAmount: toNumberOrNull(f.vatAmount),
  allocationBasis: f.allocationBasis || null,
  inCustomsValue: triState(f.inCustomsValue),
  recoverable: triState(f.recoverable)
})

const CostLinesTab = ({ shipment, readOnly, onChange }) => {
  const { t, fmt } = useI18n()
  const [concepts, setConcepts] = useState([])
  const [editing, setEditing] = useState(null) // costLineId | 'new'
  const [form, setForm] = useState(blank())

  useEffect(() => {
    getCatalog().then((c) => setConcepts(c.concepts || [])).catch(() => {})
  }, [])

  // Concepts come from the API catalog; labels from i18n, falling back to the API's.
  const apiLabel = useMemo(() => Object.fromEntries(concepts.map((c) => [c.code, c.label])), [concepts])
  const conceptLabel = (code) => {
    const key = `concepts.${code}`
    const text = t(key)
    return text === key ? apiLabel[code] || code : text
  }
  const byId = useMemo(() => Object.fromEntries(shipment.costLines.map((l) => [l.costLineId, l])), [shipment.costLines])
  const groups = useMemo(() => COST_LAYERS
    .map((layer) => ({ ...layer, lines: shipment.costLines.filter((l) => l.category === layer.key) }))
    .filter((g) => g.lines.length), [shipment.costLines])

  const pending = shipment.costLines.filter((l) => l.status === 'suggested' && !l.duplicateOf).length

  const act = async (fn, okMessage) => {
    try {
      await fn()
      if (okMessage) toast.success(okMessage)
      onChange()
    } catch (error) {
      toast.error(apiError(error, error.message || t('common.actionFailed')))
    }
  }

  const start = (line) => { setEditing(line ? line.costLineId : 'new'); setForm(line ? toForm(line) : blank()) }
  const set = (key) => (e) => setForm((f) => {
    const next = { ...f, [key]: e.target.value }
    // Picking a known concept moves the line to that concept's layer.
    if (key === 'conceptCode') {
      const concept = concepts.find((c) => c.code === e.target.value)
      if (concept) next.category = concept.category
    }
    return next
  })
  const save = () => act(async () => {
    const body = toBody(form)
    if (body.amount === null || !body.currency) throw new Error(t('costs.amountRequired'))
    if (editing === 'new') await createCostLine(shipment.shipmentId, body)
    else await updateCostLine(shipment.shipmentId, editing, body)
    setEditing(null)
  }, t('costs.saved'))

  const editRow = (key) => (
    <tr key={key} className='bg-sky-50/50'>
      <td colSpan={8} className='px-3 py-3'>
        <div className='grid gap-2 md:grid-cols-6'>
          <label className='text-xs text-slate-500 md:col-span-2'>{t('costs.form.concept')}
            <select value={form.conceptCode} onChange={set('conceptCode')} className={cellInput}>
              <option value=''>{t('costs.form.freeText')}</option>
              {concepts.map((c) => <option key={c.code} value={c.code}>{conceptLabel(c.code)}</option>)}
            </select>
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.layer')}
            <select value={form.category} onChange={set('category')} className={cellInput}>
              {COST_LAYERS.map((l) => <option key={l.key} value={l.key}>{t(`layers.${l.key}`)}</option>)}
            </select>
          </label>
          <label className='text-xs text-slate-500 md:col-span-3'>{t('costs.form.description')}
            <input value={form.conceptRaw} onChange={set('conceptRaw')} className={cellInput} />
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.amount')}
            <input type='number' step='any' value={form.amount} onChange={set('amount')} className={cellInput} />
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.currency')}
            <input value={form.currency} onChange={set('currency')} maxLength={3} className={cellInput} />
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.exchangeRate')}
            <input type='number' step='any' value={form.exchangeRate} onChange={set('exchangeRate')} placeholder={t('costs.form.exchangeRatePlaceholder')} className={cellInput} />
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.vat')}
            <input type='number' step='any' value={form.vatAmount} onChange={set('vatAmount')} className={cellInput} />
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.spreadBy')}
            <select value={form.allocationBasis} onChange={set('allocationBasis')} className={cellInput}>
              <option value=''>{t('common.default')}</option>
              {ALLOCATION_BASES.map((b) => <option key={b} value={b}>{t(`bases.${b}`)}</option>)}
            </select>
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.inCustomsValue')}
            <select value={form.inCustomsValue} onChange={set('inCustomsValue')} className={cellInput}>
              <option value=''>{t('common.default')}</option><option value='true'>{t('common.yes')}</option><option value='false'>{t('common.no')}</option>
            </select>
          </label>
          <label className='text-xs text-slate-500'>{t('costs.form.recoverable')}
            <select value={form.recoverable} onChange={set('recoverable')} className={cellInput}>
              <option value=''>{t('common.default')}</option><option value='true'>{t('common.yes')}</option><option value='false'>{t('common.no')}</option>
            </select>
          </label>
        </div>
        <div className='mt-3 flex justify-end gap-3 text-sm'>
          <button onClick={save} className='font-semibold text-brand hover:underline'>{t('common.save')}</button>
          <button onClick={() => setEditing(null)} className='text-slate-500 hover:underline'>{t('common.cancel')}</button>
        </div>
      </td>
    </tr>
  )

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='text-sm text-slate-500'>{t('costs.help')}</p>
        {!readOnly && (
          <div className='flex gap-2'>
            {pending > 0 && (
              <button onClick={() => act(() => confirmAllCostLines(shipment.shipmentId), t('costs.confirmed'))}
                className='rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50'>
                {t('costs.confirmAll', { count: pending })}
              </button>
            )}
            <button onClick={() => start(null)} disabled={editing !== null}
              className='rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
              {t('costs.add')}
            </button>
          </div>
        )}
      </div>

      <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
        <table className='min-w-full divide-y divide-slate-200 text-sm'>
          <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
            <tr>
              <th className='px-3 py-3'>{t('common.status')}</th>
              <th className='px-3 py-3'>{t('costs.concept')}</th>
              <th className='px-3 py-3'>{t('costs.source')}</th>
              <th className='px-3 py-3 text-right'>{t('costs.amount')}</th>
              <th className='px-3 py-3 text-right'>{t('costs.vat')}</th>
              <th className='px-3 py-3'>{t('costs.flags')}</th>
              <th className='px-3 py-3 text-right'>{t('costs.conf')}</th>
              <th className='px-3 py-3 text-right'>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {editing === 'new' && editRow('new')}
            {groups.length === 0 && editing !== 'new' && (
              <tr><td colSpan={8} className='px-4 py-8 text-center text-slate-400'>{t('costs.empty')}</td></tr>
            )}
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                <tr className='bg-slate-50/70'>
                  <td colSpan={8} className='px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600'>
                    <span className='mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle' style={{ background: g.color }} />
                    {t(`layers.${g.key}`)}
                  </td>
                </tr>
                {g.lines.map((l) => {
                  if (editing === l.costLineId) return editRow(l.costLineId)
                  const dup = l.duplicateOf ? byId[l.duplicateOf] : null
                  const muted = l.status === 'rejected' || dup
                  return (
                    <tr key={l.costLineId} className={`hover:bg-slate-50 ${muted ? 'opacity-60' : ''}`}>
                      <td className='px-3 py-2'><StatusBadge status={dup ? 'duplicate' : l.status} /></td>
                      <td className='px-3 py-2'>
                        <p className={`font-medium ${l.status === 'rejected' ? 'line-through' : ''}`}>{l.conceptRaw || (l.conceptCode ? conceptLabel(l.conceptCode) : '—')}</p>
                        <p className='text-xs text-slate-400'>
                          {l.conceptCode ? conceptLabel(l.conceptCode) : t('costs.unrecognised')}
                          {l.paidOnBehalf && ` · ${t('costs.paidOnBehalf')}`}
                          {dup && ` · ${t('costs.duplicateOf', { name: dup.conceptRaw || dup.conceptCode, source: dup.docType ? t(`docTypes.${dup.docType}`) : t('costs.manual') })}`}
                        </p>
                      </td>
                      <td className='px-3 py-2 text-slate-600'>
                        {l.docType ? t(`docTypes.${l.docType}`) : t('costs.manual')}
                        {l.documentName && <p className='max-w-[180px] truncate text-xs text-slate-400'>{l.documentName}</p>}
                      </td>
                      <td className='px-3 py-2 text-right tabular-nums'>
                        {fmt.money(l.amount, l.currency)}
                        {l.exchangeRate && <p className='text-xs text-slate-400'>@ {fmt.number(l.exchangeRate)}</p>}
                      </td>
                      <td className='px-3 py-2 text-right tabular-nums text-slate-500'>{l.vatAmount ? fmt.money(l.vatAmount) : '—'}</td>
                      <td className='px-3 py-2 text-xs text-slate-500'>
                        {l.inCustomsValue && <span className='mr-1 rounded bg-slate-100 px-1.5 py-0.5'>{t('costs.flagCustomsValue')}</span>}
                        {l.recoverable && <span className='mr-1 rounded bg-slate-100 px-1.5 py-0.5'>{t('costs.flagRecoverable')}</span>}
                        {l.allocationBasis && <span className='rounded bg-slate-100 px-1.5 py-0.5'>{t('costs.flagBy', { basis: t(`bases.${l.allocationBasis}`) })}</span>}
                      </td>
                      <td className='px-3 py-2 text-right tabular-nums text-slate-500'>{fmt.pct(l.confidence)}</td>
                      <td className='px-3 py-2'>
                        {!readOnly && (
                          <div className='flex justify-end gap-3 whitespace-nowrap'>
                            {(l.status !== 'confirmed' || dup) && (
                              <button onClick={() => act(() => confirmCostLine(shipment.shipmentId, l.costLineId))}
                                className='font-semibold text-status-ready hover:underline'>{dup ? t('costs.keep') : t('costs.confirm')}</button>
                            )}
                            {l.status !== 'rejected' && (
                              <button onClick={() => act(() => rejectCostLine(shipment.shipmentId, l.costLineId))}
                                className='font-semibold text-status-incomplete hover:underline'>{t('costs.reject')}</button>
                            )}
                            <button onClick={() => start(l)} className='font-semibold text-brand hover:underline'>{t('common.edit')}</button>
                            {!l.documentId && (
                              <button onClick={() => window.confirm(t('costs.confirmDelete')) && act(() => deleteCostLine(shipment.shipmentId, l.costLineId))}
                                className='text-slate-500 hover:underline'>{t('common.delete')}</button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CostLinesTab
